import { useState, useEffect, useCallback } from 'react';
import { Menu, X, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9013cffc-5815-4696-af30-6ef829e3ad5d/artifacts/390zcaea_logo.png";

const NAV_LINKS = [
  { label: "History", href: "#history" },
  { label: "Artists", href: "#artists" },
  { label: "Genres", href: "#genres" },
  { label: "Media", href: "#media" },
  { label: "Events", href: "#events" },
  { label: "Resources", href: "#resources" },
  { label: "Contact", href: "#contact" },
];

function NavLink({ link, onClick }) {
  return (
    <button
      data-testid={`nav-${link.label.toLowerCase()}-link`}
      onClick={() => onClick(link.href)}
      className="text-sm font-medium text-[#a3b1c6] hover:text-[#d4af37] transition-colors duration-200 tracking-wide uppercase"
    >
      {link.label}
    </button>
  );
}

function MobileMenu({ onNavClick, onAdminClick }) {
  return (
    <div className="lg:hidden backdrop-blur-xl bg-[#0a1628]/95 border-t border-[#d4af37]/10 px-6 py-6 space-y-4" data-testid="mobile-menu">
      {NAV_LINKS.map((link) => (
        <button
          key={link.href}
          onClick={() => onNavClick(link.href)}
          className="block w-full text-left text-[#a3b1c6] hover:text-[#d4af37] py-2 text-base transition-colors uppercase tracking-wide"
        >
          {link.label}
        </button>
      ))}
      <button onClick={onAdminClick} className="block w-full text-left text-[#a3b1c6] hover:text-[#d4af37] py-2 text-base">
        Admin Panel
      </button>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = useCallback((href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleAdminClick = useCallback(() => {
    setMobileOpen(false);
    navigate('/admin');
  }, [navigate]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled ? 'backdrop-blur-xl bg-[#0a1628]/90 border-b border-[#d4af37]/20 shadow-lg' : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <a href="#hero" onClick={() => handleNavClick('#hero')} className="flex items-center gap-3" data-testid="nav-logo">
          <img src={LOGO_URL} alt="SitarMusic" className="h-10 md:h-12 w-auto" />
          <span className="font-['Cormorant_Garamond'] text-xl md:text-2xl font-medium text-[#d4af37] tracking-wide hidden sm:block">
            SitarMusic
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <NavLink key={link.href} link={link} onClick={handleNavClick} />
          ))}
          <button data-testid="nav-admin-link" onClick={handleAdminClick} className="flex items-center gap-1.5 text-sm text-[#a3b1c6] hover:text-[#d4af37] transition-colors">
            <Settings className="w-4 h-4" />
          </button>
        </div>

        <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-[#a3b1c6] hover:text-[#d4af37] transition-colors">
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {mobileOpen && <MobileMenu onNavClick={handleNavClick} onAdminClick={handleAdminClick} />}
    </header>
  );
}
